import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, Sparkles, Lock, MessageSquare, Home, CreditCard, KeyRound, PhoneCall } from "lucide-react";

const PILLARS = [
  {
    icon: BadgeCheck,
    title: "Verified identity",
    body: "Every guest and host can complete a one-time KYC check — a photo of a government-issued ID plus a selfie for liveness. Approved accounts get a copper badge next to their name across the site.",
    link: { to: "/verify", label: "Verify your account" },
  },
  {
    icon: Home,
    title: "Verified property host",
    body: "Hosts who pass identity verification and have at least one active listing display a Verified Host badge. Guests booking a verified host see the badge on every card, before they even open the listing.",
  },
  {
    icon: Sparkles,
    title: "Superhost recognition",
    body: "A host earns the Superhost badge once they've completed at least 10 stays and hold an average rating of 4.5 stars or higher. It's re-evaluated automatically so the recognition stays honest.",
  },
  {
    icon: CreditCard,
    title: "Safer payments",
    body: "Mobile-money payments (MTN, Airtel, Zamtel) settle to EasyStay first — hosts are paid after check-in, so a scam listing can never collect and disappear. Card payments run through Stripe with 3-D Secure.",
  },
  {
    icon: KeyRound,
    title: "Two safe key handoffs",
    body: "Digital keys are shared through EasyStay (with an SMS backup via Twilio) so the exchange is logged. Physical keys are collected at a published address — never at a private meet-up requested via chat.",
  },
  {
    icon: Lock,
    title: "Private by default",
    body: "Selfies, IDs and phone numbers stay inside the platform. We never share your data with advertisers, and you can opt out of SMS notifications by texting STOP to our Twilio number.",
    link: { to: "/privacy", label: "Read the privacy policy" },
  },
];

const REPORT = [
  { label: "Report a listing or user", href: "mailto:trust@easystayzm.com" },
  { label: "Refund or payment dispute", href: "mailto:support@easystayzm.com" },
  { label: "Emergency during a stay", href: "tel:+260991000000", note: "Zambian police: 991 · Ambulance: 992" },
];

export default function TrustSafety() {
  return (
    <div className="mx-auto max-w-5xl px-6 py-12 lg:px-8" data-testid="trust-safety-page">
      {/* Hero */}
      <div className="rounded-3xl bg-forest p-8 text-white lg:p-12">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
          <ShieldCheck className="h-4 w-4" /> Trust & Safety
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold sm:text-5xl">Book with confidence</h1>
        <p className="mt-4 max-w-2xl text-white/80">
          EasyStay is a Zambian marketplace, but the promise is universal: you always know who you&apos;re booking with,
          your money is held safely, and if anything goes wrong there&apos;s a real human to call.
        </p>
      </div>

      {/* Pillars */}
      <section className="mt-10 grid gap-6 md:grid-cols-2">
        {PILLARS.map((p) => (
          <div key={p.title} className="rounded-3xl bg-white p-6 card-shadow">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-copper/10 text-copper">
              <p.icon className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-display text-lg font-bold text-slate-900">{p.title}</h2>
            <p className="mt-2 text-sm text-slate-700">{p.body}</p>
            {p.link && (
              <Link to={p.link.to} data-testid={`trust-link-${p.title.split(" ")[0].toLowerCase()}`}
                className="mt-3 inline-block text-sm font-semibold text-copper hover:underline">
                {p.link.label} →
              </Link>
            )}
          </div>
        ))}
      </section>

      {/* Report */}
      <section className="mt-12 rounded-3xl bg-sand/60 p-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
          <PhoneCall className="h-3.5 w-3.5" /> Get help fast
        </div>
        <h2 className="mt-2 font-display text-2xl font-bold text-slate-900">Report a concern</h2>
        <p className="mt-2 text-sm text-slate-700">
          Every message reaches a real EasyStay team member. We aim to reply within one business day.
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {REPORT.map((r) => (
            <a key={r.label} href={r.href}
              className="rounded-2xl bg-white p-4 text-sm shadow-sm hover:shadow-md transition">
              <MessageSquare className="h-4 w-4 text-copper" />
              <div className="mt-2 font-semibold text-slate-900">{r.label}</div>
              {r.note && <div className="mt-1 text-xs text-slate-500">{r.note}</div>}
            </a>
          ))}
        </div>
      </section>

      <div className="mt-10 text-center text-xs text-slate-500">
        See how we handle your data in the <Link to="/privacy" className="text-copper hover:underline">Privacy Policy</Link>.
      </div>
    </div>
  );
}
